import React, { lazy, ReactNode, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Home } from './pages/Home';
import { PublicLanding } from './pages/PublicLanding';
import { PublicForm } from './pages/PublicForm';
import { ThankYou } from './pages/ThankYou';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { ResetPassword } from './pages/auth/ResetPassword';
import { NotFound } from './pages/NotFound';
import { Toaster } from './components/ui/Toast';

/**
 * La zona privada (CRM, constructor, arrastrar y soltar, administración) se
 * carga bajo demanda: un visitante que solo abre una landing pública nunca
 * descarga ese código.
 */
const AppLayout = lazy(() => import('./layouts/AppLayout').then(m => ({
  default: m.AppLayout
})));
const Dashboard = lazy(() => import('./pages/app/Dashboard').then(m => ({
  default: m.Dashboard
})));
const Leads = lazy(() => import('./pages/app/Leads').then(m => ({
  default: m.Leads
})));
const LeadDetail = lazy(() => import('./pages/app/LeadDetail').then(m => ({
  default: m.LeadDetail
})));
const Pipeline = lazy(() => import('./pages/app/Pipeline').then(m => ({
  default: m.Pipeline
})));
const Trash = lazy(() => import('./pages/app/Trash').then(m => ({
  default: m.Trash
})));
const Forms = lazy(() => import('./pages/app/Forms').then(m => ({
  default: m.Forms
})));
const FormEditor = lazy(() => import('./pages/app/FormEditor').then(m => ({
  default: m.FormEditor
})));
const Landings = lazy(() => import('./pages/app/Landings').then(m => ({
  default: m.Landings
})));
const LandingEditor = lazy(() => import('./pages/app/LandingEditor').then(m => ({
  default: m.LandingEditor
})));
const Team = lazy(() => import('./pages/app/Team').then(m => ({
  default: m.Team
})));
const Settings = lazy(() => import('./pages/app/Settings').then(m => ({
  default: m.Settings
})));
const Integrations = lazy(() => import('./pages/app/Integrations').then(m => ({
  default: m.Integrations
})));

/** Indicador de carga compartido mientras llega el código de la zona privada. */
function Loading() {
  return <div className="flex min-h-screen w-full items-center justify-center bg-background" aria-busy="true" data-id="element-0" data-mp="85l0op2u8u85">
      <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" data-id="element-1" data-mp="3gl4qtf87pwiz" />
      <span className="sr-only" data-id="element-2" data-mp="3bmjo995hn0rd">Cargando tu espacio de trabajo…</span>
    </div>;
}

/** Protección de la zona privada con la sesión real de Supabase. */
function RequireAuth({
  children
}: {
  children: ReactNode;
}) {
  const {
    session,
    restoring
  } = useAuth();
  const location = useLocation();
  if (restoring) return <Loading data-id="element-3" data-mp="2xtv8cgggrxf9" />;
  if (!session) {
    return <Navigate to="/login" state={{
      from: location.pathname
    }} replace data-id="element-4" data-mp="2xssjee2pc51z" />;
  }
  return <>{children}</>;
}
export function App() {
  return <BrowserRouter data-id="element-5" data-mp="18dhq9zh8ks3w">
      <AuthProvider data-id="element-6" data-mp="18cznek02jfn0">
        <Routes data-id="element-7" data-mp="18cgchn8u5rih">
          {/* Zona pública */}
          <Route path="/" element={<Home data-id="element-9" data-mp="1d59linbc7m0d" />} data-id="element-8" data-mp="1d4zpoj3m2kmr" />
          <Route path="/landing/:slug" element={<PublicLanding data-id="element-11" data-mp="yfvbdczxt0jx" />} data-id="element-10" data-mp="1d5srr6rmklyk" />
          <Route path="/formulario/:slug" element={<PublicForm data-id="element-13" data-mp="ydikubg45hv9" />} data-id="element-12" data-mp="ygmtbl0m2zrq" />
          <Route path="/gracias/:slug" element={<ThankYou data-id="element-15" data-mp="13fgapoj0rax6" />} data-id="element-14" data-mp="13eeuz6r26094" />
          <Route path="/login" element={<Login data-id="element-17" data-mp="1r4inoj2pkflc" />} data-id="element-16" data-mp="13clobrgg3g14" />
          <Route path="/registro" element={<Register data-id="element-19" data-mp="1r4siql2m1cxw" />} data-id="element-18" data-mp="1r3zim2y5ploq" />
          <Route path="/recuperar-contrasena" element={<ForgotPassword data-id="element-21" data-mp="1w2araacptq5d" />} data-id="element-20" data-mp="1r2g1ka4of6lw" />
          <Route path="/restablecer-contrasena" element={<ResetPassword data-id="element-23" data-mp="1i440tfa3isfq" />} data-id="element-22" data-mp="1w2kig19hy3cn" />

          {/* Zona privada */}
          <Route path="/app" element={<RequireAuth data-id="element-25" data-mp="1m4wa28iywxc0">
                <Suspense fallback={<Loading data-id="element-27" data-mp="1m3l6o9tfprlf" />} data-id="element-26" data-mp="1m44rbyagyu35">
                  <AppLayout data-id="element-28" data-mp="1m70qfpy1zot6" />
                </Suspense>
              </RequireAuth>} data-id="element-24" data-mp="1i5ngb3nxgzgg">
            <Route index element={<Navigate to="/app/dashboard" replace data-id="element-30" data-mp="aq0xqt7sn7ym" />} data-id="element-29" data-mp="6n4csvus6zri" />
            <Route path="dashboard" element={<Suspense fallback={<Loading data-id="element-33" data-mp="qwvp67ht2b37" />} data-id="element-32" data-mp="ano6tqk25n9f"><Dashboard data-id="element-34" data-mp="qxnn3oyvkkwn" /></Suspense>} data-id="element-31" data-mp="aqsljhv5g1ec" />
            <Route path="leads" element={<Suspense fallback={<Loading data-id="element-37" data-mp="m6p1qaz14wmj" />} data-id="element-36" data-mp="qyyqhnoerqn8"><Leads data-id="element-38" data-mp="m5x6505fmyw9" /></Suspense>} data-id="element-35" data-mp="qyq9bjl95z68" />
            <Route path="leads/:id" element={<Suspense fallback={<Loading data-id="element-41" data-mp="h72o40fahed7" />} data-id="element-40" data-mp="m4m1yz82rp4m"><LeadDetail data-id="element-42" data-mp="h6kkuju7lzvs" /></Suspense>} data-id="element-39" data-mp="m4uid13epckk" />
            <Route path="pipeline" element={<Suspense fallback={<Loading data-id="element-45" data-mp="c8renqswp5vi" />} data-id="element-44" data-mp="c89ckdjkbxfo"><Pipeline data-id="element-46" data-mp="c90x8tbmgar6" /></Suspense>} data-id="element-43" data-mp="h7lxgsqviufm" />
            <Route path="papelera" element={<Suspense fallback={<Loading data-id="element-49" data-mp="19wjbgjo1y52k" />} data-id="element-48" data-mp="19vzylhd9e6ve"><Trash data-id="element-50" data-mp="19uyj90p7my7v" /></Suspense>} data-id="element-47" data-mp="ca2ifqmcqdmm" />
            <Route path="formularios" element={<Suspense fallback={<Loading data-id="element-53" data-mp="14wnbqwme0zst" />} data-id="element-52" data-mp="14x6idh6l81rj"><Forms data-id="element-54" data-mp="14xyequ80e3iv" /></Suspense>} data-id="element-51" data-mp="19v88un6cf3d1" />
            <Route path="formularios/nuevo" element={<Suspense fallback={<Loading data-id="element-57" data-mp="zwskgrypa878" />} data-id="element-56" data-mp="zxamk581ngn2"><Forms data-id="element-58" data-mp="zwiuv5hki322" /></Suspense>} data-id="element-55" data-mp="14uu012wx3pdy" />
            <Route path="formularios/:id" element={<Suspense fallback={<Loading data-id="element-61" data-mp="vvgrqdkj56ut" />} data-id="element-60" data-mp="vx05nxipr5tf"><FormEditor data-id="element-62" data-mp="vvp1vxp9py2b" /></Suspense>} data-id="element-59" data-mp="zzojkjmab2x9" />
            <Route path="landings" element={<Suspense fallback={<Loading data-id="element-65" data-mp="3kpm8kgrq7fxg" />} data-id="element-64" data-mp="3kp43cun35rde"><Landings data-id="element-66" data-mp="3kpdwus7cagnu" /></Suspense>} data-id="element-63" data-mp="3ko2oef2y8kqe" />
            <Route path="landings/nueva" element={<Suspense fallback={<Loading data-id="element-69" data-mp="3gl632gm0yair" />} data-id="element-68" data-mp="3glo9u76bc74x"><Landings data-id="element-70" data-mp="3gok9bzxt73vh" /></Suspense>} data-id="element-67" data-mp="3gmpjc77oklkj" />
            <Route path="landings/:id/editor" element={<Suspense fallback={<Loading data-id="element-73" data-mp="1w2c5hha38e7s" />} data-id="element-72" data-mp="1w5zuc34l8cg6"><LandingEditor data-id="element-74" data-mp="2060abs7c4vpc" /></Suspense>} data-id="element-71" data-mp="1w4ykg1zb5w01" />
            <Route path="equipo" element={<Suspense fallback={<Loading data-id="element-77" data-mp="203nt5h9eipdc" />} data-id="element-76" data-mp="206a1hj4498wm"><Team data-id="element-78" data-mp="2553elafhqxw9" /></Suspense>} data-id="element-75" data-mp="2071y8x9g9coh" />
            <Route path="configuracion" element={<Suspense fallback={<Loading data-id="element-81" data-mp="29vtl4qznl6sj" />} data-id="element-80" data-mp="2530ge346pygg"><Settings data-id="element-82" data-mp="29vjwb6qch5of" /></Suspense>} data-id="element-79" data-mp="25424b86auffl" />
            <Route path="integraciones" element={<Suspense fallback={<Loading data-id="element-85" data-mp="2ev6ja7vz60af" />} data-id="element-84" data-mp="29xo96e681f0u"><Integrations data-id="element-86" data-mp="2evg1386v9151" /></Suspense>} data-id="element-83" data-mp="29wlhi412r8jv" />
          </Route>

          {/*
            Enlace público definitivo: sistemaava.com/{slug}. Debe declararse
            DESPUÉS de las rutas fijas (react-router prioriza las estáticas) y
            antes del 404. Si el slug no existe, LandingView muestra su propio
            aviso: nunca se redirige a la portada.
           */}
          <Route path="/:slug" element={<PublicLanding data-id="element-88" data-mp="tid8lc988xqp" />} data-id="element-87" data-mp="thlbty3w8tyu" />

          {/* Nunca usar la portada como respaldo de una ruta desconocida. */}
          <Route path="*" element={<NotFound data-id="element-90" data-mp="yh6jcwx4qan6" />} data-id="element-89" data-mp="yhprxn0w3moj" />
        </Routes>
        {/* Confirmaciones de copiado, publicación y guardado */}
        <Toaster position="bottom-right" data-id="element-91" data-mp="21ru4n8hwe5d8" />
      </AuthProvider>
    </BrowserRouter>;
}